/**
 * Hook para gerenciar operações de investigação
 * Baseado em best practices 2025 para custom hooks
 */

import { useState, useCallback } from 'react';
import { investigacaoAPI, ApiError } from '../services/api';
import type { Investigation, CreateInvestigationPayload } from '../types/investigation';

interface UseInvestigationOptions {
  onSuccess?: (investigation: Investigation) => void;
  onError?: (error: ApiError) => void;
}

interface UseInvestigationReturn {
  investigation: Investigation | null;
  investigations: Investigation[];
  loading: boolean;
  error: ApiError | null;

  // Actions
  fetchInvestigation: (id: string) => Promise<void>;
  fetchAllInvestigations: () => Promise<void>;
  createInvestigation: (payload: CreateInvestigationPayload) => Promise<Investigation | null>;
  expandNode: (investigationId: string, nodeId: string) => Promise<void>;
  deleteInvestigation: (id: string) => Promise<void>;
  clearError: () => void;
}

/**
 * Hook customizado para gerenciar investigações
 *
 * @param options - Callbacks opcionais de sucesso/erro
 * @returns Estado e funções para manipular investigações
 *
 * @example
 * const { investigation, loading, fetchInvestigation } = useInvestigation();
 *
 * useEffect(() => {
 *   fetchInvestigation('123');
 * }, []);
 */
export function useInvestigation(
  options: UseInvestigationOptions = {}
): UseInvestigationReturn {
  const [investigation, setInvestigation] = useState<Investigation | null>(null);
  const [investigations, setInvestigations] = useState<Investigation[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const { onSuccess, onError } = options;

  /**
   * Buscar investigação específica por ID
   */
  const fetchInvestigation = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);

    try {
      const data = await investigacaoAPI.buscar(id);
      setInvestigation(data);
      onSuccess?.(data);
    } catch (err) {
      const apiError = err as ApiError;
      setError(apiError);
      onError?.(apiError);
    } finally {
      setLoading(false);
    }
  }, [onSuccess, onError]);

  /**
   * Buscar todas as investigações
   */
  const fetchAllInvestigations = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await investigacaoAPI.listar();
      setInvestigations(data);
    } catch (err) {
      const apiError = err as ApiError;
      setError(apiError);
      onError?.(apiError);
    } finally {
      setLoading(false);
    }
  }, [onError]);

  /**
   * Criar nova investigação
   */
  const createInvestigation = useCallback(async (
    payload: CreateInvestigationPayload
  ): Promise<Investigation | null> => {
    setLoading(true);
    setError(null);

    try {
      const data = await investigacaoAPI.iniciar(payload);
      setInvestigation(data);

      // Adicionar à lista de investigações se já carregada
      if (investigations.length > 0) {
        setInvestigations((prev) => [data, ...prev]);
      }

      onSuccess?.(data);
      return data;
    } catch (err) {
      const apiError = err as ApiError;
      setError(apiError);
      onError?.(apiError);
      return null;
    } finally {
      setLoading(false);
    }
  }, [investigations.length, onSuccess, onError]);

  /**
   * Expandir nó de uma investigação
   */
  const expandNode = useCallback(async (investigationId: string, nodeId: string) => {
    setLoading(true);
    setError(null);

    try {
      const data = await investigacaoAPI.expandir(investigationId, nodeId);
      setInvestigation(data);

      // Atualizar na lista também se existe
      setInvestigations((prev) =>
        prev.map((inv) => {
          const id = inv.investigacao_id || inv.id;
          return id === investigationId ? data : inv;
        })
      );

      onSuccess?.(data);
    } catch (err) {
      const apiError = err as ApiError;
      setError(apiError);
      onError?.(apiError);
    } finally {
      setLoading(false);
    }
  }, [onSuccess, onError]);

  /**
   * Deletar investigação
   */
  const deleteInvestigation = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);

    try {
      await investigacaoAPI.deletar(id);

      // Remover da lista
      setInvestigations((prev) =>
        prev.filter((inv) => {
          const invId = inv.investigacao_id || inv.id;
          return invId !== id;
        })
      );

      // Limpar investigação atual se for a mesma
      if (investigation && (investigation.investigacao_id === id || investigation.id === id)) {
        setInvestigation(null);
      }
    } catch (err) {
      const apiError = err as ApiError;
      setError(apiError);
      onError?.(apiError);
    } finally {
      setLoading(false);
    }
  }, [investigation, onError]);

  /**
   * Limpar erro manualmente
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    investigation,
    investigations,
    loading,
    error,
    fetchInvestigation,
    fetchAllInvestigations,
    createInvestigation,
    expandNode,
    deleteInvestigation,
    clearError,
  };
}
